import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import { BsBoxArrowUp } from 'react-icons/bs';
import { useTranslation } from 'react-i18next';
import LoaderMePos from '../../../loader/LoaderMePos';
import { CSVLink } from 'react-csv';
import { getTypeForDisplay } from '../utils/helpers';

const ExportSalesBtn = ({
                          mainColumns = [],
                          tableRowsRaw = [],
                          tableRowsArray = [],
                          currency = '',
                          filenameBase = 'sales_report',
                          selectedFiltersList = [],
                          summary = null,
                          className = ''
                        }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const timeoutRef = useRef(null);
  const csvLinkRef = useRef(null);

  // choose separator by locale (if 1.1 -> "1,1", use ';')
  const detectSeparator = () => {
    try {
      const s = (1.1).toLocaleString();
      return s.indexOf(',') !== -1 ? ';' : ',';
    } catch {
      return ',';
    }
  };

  const separator = useMemo(detectSeparator, []);

  const rowsMatrix = useMemo(() => {
    const rows = [];
    let header = [];
    if (mainColumns.length) {
      header = mainColumns.map(c => {
        if (c.titleKey) return t(c.titleKey);
        if (c.title) {
          return t(String(c.title));
        }
        return String(c.key ?? '');
      });
    } else if (tableRowsArray && tableRowsArray.length) {
      header = tableRowsArray[0].map((_, i) => `col_${i+1}`);
    } else if (tableRowsRaw && tableRowsRaw.length) {
      header = Object.keys(tableRowsRaw[0]);
    }
    if (header.length) rows.push(header);

    if (tableRowsRaw && tableRowsRaw.length) {
      if (mainColumns && mainColumns.length) {
        tableRowsRaw.forEach(r => {
          const row = mainColumns.map(col => {
            const rawVal = r[col.key];
            if (col.formatter && typeof col.formatter === 'function') {
              try { return col.formatter(rawVal, currency); } catch { return rawVal ?? ''; }
            }
            return rawVal ?? '';
          });
          rows.push(row);
        });
      } else {
        tableRowsRaw.forEach(r => rows.push(Object.keys(r).map(k => r[k] ?? '')));
      }
    } else if (tableRowsArray && tableRowsArray.length) {
      tableRowsArray.forEach(a => rows.push(a.map(c => c ?? '')));
    }

    return rows;
  }, [mainColumns, tableRowsRaw, tableRowsArray, selectedFiltersList, summary, currency]);

  const isExportable = useMemo(() => {
    if (!Array.isArray(rowsMatrix) || rowsMatrix.length === 0) return false;
    const first = rowsMatrix[0];
    const firstRowIsHeader = Array.isArray(first) && first.length > 0 && first.every(v => typeof v === 'string');
    return firstRowIsHeader ? rowsMatrix.length > 1 : rowsMatrix.length > 0;
  }, [rowsMatrix]);

  const finalFileName = useMemo(() => {
    const now = new Date();
    const datePart = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}`;
    const typeForDisplay = getTypeForDisplay(filenameBase);
    return `${t(typeForDisplay)} (${datePart}).csv`;
  }, [filenameBase, t]);

  const handleClick = (e) => {
    if (loading || !isExportable) {
      e.preventDefault();
      return;
    }
    setLoading(true);

    timeoutRef.current = setTimeout(() => {
      setLoading(false);
      timeoutRef.current = null;
    }, 900);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, []);

  if (!isExportable) {
    return (
      <>
        <Button
          variant="outline-pos-main"
          className={`d-flex align-items-center justify-content-center rounder-4px w-100 ${className}`}
          style={{ padding: '11px' }}
          disabled={true}
        >
          <BsBoxArrowUp className="me-2" style={{ pointerEvents: 'none' }} />
          {t('statistic.export')}
        </Button>
      </>
    );
  }

  return (
    <>
      <LoaderMePos opacity={50} toggle={loading} />
      <CSVLink
        data={rowsMatrix}
        filename={finalFileName}
        uFEFF={true}
        separator={separator}
        enclosingCharacter={'"'}
        style={{ textDecoration: 'none' }}
        ref={csvLinkRef}
      >
        <Button
          variant="outline-pos-main"
          className={`d-flex align-items-center justify-content-center rounder-4px w-100 ${className}`}
          style={{padding: '11px'}}
          onClick={handleClick}
          disabled={loading || !isExportable}
        >
          <BsBoxArrowUp className="me-2" style={{ pointerEvents: 'none' }} />
          {t('statistic.export')}
        </Button>
      </CSVLink>
    </>
  );
};

export default ExportSalesBtn;